import { api } from "../api";
import { bridge, BridgeActions, BridgeChannels } from "../bridge";
import { retryWithBackoff } from "../retry";

/**
 * Pushes the current web session down into the native shell.
 *
 * This is the inverse of `signInFromNativeAuthToken`. The established flow is
 * native → web: Flutter runs the OAuth, hands the WebView an ID token, and the
 * web exchanges it for a session. But the email-code flow is handled entirely
 * in the web client, so inside the app the WebView ends up signed in while the
 * Flutter shell — which owns push tokens, IAP and the native account sheet —
 * is still signed out. This closes that gap.
 *
 * We mint a *fresh* custom token from GET /api/user/auth/token rather than
 * reusing the one the code flow already spent: it keeps the two sessions
 * independent, and the endpoint already exists for exactly this purpose.
 *
 * No-ops on web. Never throws — a shell that fails to catch up is a degraded
 * state (push may not register), not a reason to fail a sign-in the user has
 * already completed.
 *
 * Loop safety: Flutter signing in fires its own authStateChanges, which pushes
 * an `auth/stateChanged` event back to the web. AuthProvider's handler bails
 * early when the uid already matches the current one, so this does not cycle.
 */
export async function signInNativeShellFromWebSession(): Promise<boolean> {
  if (!bridge.isNative) return false;

  try {
    const response = await retryWithBackoff(
      () => api.get<{ token: string }>("/user/auth/token"),
      { attempts: 3, baseDelayMs: 1000 },
    );
    if (!response.success || !response.data?.token) {
      console.error("[AuthProvider] No custom token for native shell handoff");
      return false;
    }

    // Rides the existing nativeAuth handler as a fourth method rather than its
    // own action — same shape as the 'email' method, which also carries its
    // inputs under `credentials`.
    const result = await bridge.request<{ success: boolean; error?: string }>(
      BridgeChannels.AUTH,
      BridgeActions.NATIVE_AUTH,
      { method: "customToken", credentials: { token: response.data.token } },
    );

    if (!result?.success) {
      console.error(
        "[AuthProvider] Native shell rejected custom token:",
        result?.error,
      );
      return false;
    }
    return true;
  } catch (e) {
    // Older app builds don't know the 'customToken' method and reject it as an
    // unknown one. The web session is still valid, so treat it as a soft
    // failure.
    console.error("[AuthProvider] Native shell handoff failed:", e);
    return false;
  }
}
